/**
 * PLANS GESTIC — source de vérité UNIQUE des tarifs.
 *
 * Utilisée par :
 *  - la landing publique (affichage des prix + transparence des frais)
 *  - l'API checkout (création des produits/prix Stripe par lookup_key)
 *  - le webhook (mapping prix Stripe → plan allowed_users)
 *
 * Les prix sont TTC, frais Stripe INCLUS — le détail est affiché
 * publiquement sur la landing (section transparence). Si tu changes un
 * prix ici, un NOUVEAU price Stripe est créé au prochain checkout
 * (lookup_key versionné par le montant), les abonnés existants gardent
 * leur ancien prix.
 *
 * Frais Stripe (France, cartes UE, tarif standard 2026) :
 *  - 1,5 % + 0,25 € par paiement carte européenne
 *  - + 0,7 % Stripe Billing (gestion des abonnements/essais)
 * Le fixe de 0,25 € pèse lourd sur les petits montants — c'est LA
 * raison pour laquelle un abonnement à 2 € laisse ~14 % à Stripe.
 */

export type PlanKey = "pro" | "light";

export interface GesticPlan {
  key: PlanKey;
  /** Nom public. */
  name: string;
  /** Description du produit Stripe, affichée sous le nom sur la page de
   *  paiement. Synchronisée sur le produit existant au checkout suivant. */
  description: string;
  /** Prix mensuel en CENTIMES (source de vérité). */
  priceCents: number;
  /** lookup_key Stripe du price mensuel (versionné par montant). */
  lookupKey: string;
  /** Valeur écrite dans allowed_users.plan quand l'abonnement est actif. */
  appPlan: string;
  /** Jours d'essai gratuit (Stripe trial_period_days). */
  trialDays: number;
}

export const GESTIC_PLANS: Record<PlanKey, GesticPlan> = {
  pro: {
    key: "pro",
    name: "Gestic Pro",
    description:
      "Le budget complet, au centime près. Dépenses et revenus pointés sur ton solde bancaire, prévisionnel chaîné mois par mois, charges fixes, budgets, épargne et patrimoine, vie commune et module Véhicule. Sans publicité, fait en France.",
    priceCents: 399,
    lookupKey: "gestic_pro_monthly_399",
    // Plan d'abonnement : les modules annoncés (lib/auth/modules-plan.ts),
    // pas `pro_gestic` qui ouvre tout aux comptes gérés à la main.
    appPlan: "pro",
    trialDays: 14,
  },
  light: {
    key: "light",
    name: "Gestic Light",
    description:
      "Les grandes lignes, la vraie vision. Revenus, charges et enveloppes en fourchettes, recalage du solde sans rien pointer, le prévisionnel de Pro et le suivi de ton patrimoine. Sans publicité, fait en France.",
    priceCents: 200,
    lookupKey: "gestic_light_monthly_200",
    appPlan: "light",
    trialDays: 14,
  },
};

/** Frais Stripe estimés pour un paiement carte UE (centimes). */
export function stripeFeesCents(priceCents: number): number {
  const percent = priceCents * 0.015; // 1,5 % carte UE
  const fixed = 25; // 0,25 € par transaction
  const billing = priceCents * 0.007; // 0,7 % Stripe Billing
  return Math.round(percent + fixed + billing);
}

/** Net encaissé par Gestic après frais Stripe (centimes). */
export function netCents(priceCents: number): number {
  return priceCents - stripeFeesCents(priceCents);
}

export function formatCents(cents: number): string {
  return (cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
